import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useUIStore } from '@/store/ui';

export function useMobileScrollDetection() {
  const uiStore = useUIStore();
  const isMobile = ref(uiStore.isMobile);
  
  // Scroll detection state
  const mainRef = ref<HTMLElement | null>(null);
  const isHeaderCollapsed = ref(false);
  let scrollTimeout: NodeJS.Timeout | null = null;
  let lastScrollY = 0;
  let scrollStartTime = 0;

  // Track bounce/rubber-band effect at bottom
  let isAtBottomSticky = false;
  let bottomReachedTime = 0;

  // Scroll detection function
  function handleMobileScroll(event: Event) {
    if (!isMobile.value) {
      return;
    }
    
    const target = event.target as HTMLElement;
    const currentScrollY = target.scrollTop;
    const now = Date.now();
    
    const isModalOpen = document.querySelector('.mobile-page-template');
    if (isModalOpen) {
      if (isHeaderCollapsed.value) {
        isHeaderCollapsed.value = false;
      }
      return;
    }
    
    // Improved throttling - only process if enough time has passed
    if (now - scrollStartTime < 50) {
      return;
    }
    
    scrollStartTime = now;
    
    const scrollThreshold = 10; // Minimum scroll movement to trigger action
    const collapseThreshold = 50; // Scroll position before collapsing
    const upwardScrollThreshold = 15; // Reduced threshold for more responsive upward scroll detection
    const bottomBufferZone = 50; // Reduced buffer zone for better responsiveness
    const bounceDelay = 200; // Delay to prevent bounce-triggered expansion (reduced from 500ms)
    
    // Only process if there's meaningful scroll movement
    if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
      return;
    }

    const isScrollingDown = currentScrollY > lastScrollY;
    const isScrollingUp = currentScrollY < lastScrollY;
    const scrollDelta = Math.abs(currentScrollY - lastScrollY);

    // Check scroll position states
    const isAtBottom = currentScrollY + target.clientHeight >= target.scrollHeight - 10;
    const isNearBottom = currentScrollY + target.clientHeight >= target.scrollHeight - bottomBufferZone;
    const scrollableHeight = target.scrollHeight - target.clientHeight;
    const isAtTop = currentScrollY <= 10;
    const timeSinceBottom = now - bottomReachedTime;
    
    // Track when we reach the bottom (only when scrolling down)
    if ((isAtBottom || isNearBottom) && isScrollingDown) {
      isAtBottomSticky = true;
      bottomReachedTime = now;
    } 
    // Also clear if we're significantly away from bottom
    else if (scrollableHeight > 0 && currentScrollY < scrollableHeight - bottomBufferZone * 2) {
      isAtBottomSticky = false;
    }
    
    // Collapse header when scrolling down (but not at bottom or top)
    if (isScrollingDown && currentScrollY > collapseThreshold && scrollDelta > scrollThreshold && !isAtBottom && !isAtTop) {
      if (!isHeaderCollapsed.value) {
        isHeaderCollapsed.value = true;
      }
    } 
    // Show header when scrolling up
    else if (isScrollingUp && scrollDelta > upwardScrollThreshold) {
      // Always show header when at top
      if (isAtTop) {
        if (isHeaderCollapsed.value) {
          isHeaderCollapsed.value = false;
        }
        isAtBottomSticky = false; // Clear bottom sticky when at top
      }
      // If we just reached bottom and scrolling up very quickly, might be bounce
      else if (isAtBottomSticky && timeSinceBottom < bounceDelay && isAtBottom && scrollDelta < 20) {
        // Likely bounce - don't expand yet, but clear the flag for next scroll
        isAtBottomSticky = false;
      }
      // Expand header on upward scroll (normal case)
      else {
        if (isHeaderCollapsed.value) {
          isHeaderCollapsed.value = false;
        }
        // Clear bottom sticky flag when intentionally scrolling up
        isAtBottomSticky = false;
      }
    }

    lastScrollY = currentScrollY;
    
    // Clear any existing timeout
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    
    // Set a longer timeout to prevent rapid state changes
    scrollTimeout = setTimeout(() => {
      // Timeout completed - no action needed
    }, 300);
  }

  // Setup scroll listeners
  function setupScrollListeners() {
    if (isMobile.value && mainRef.value) {
      const scrollOptions = {
        passive: true,
        capture: false
      };
      
      mainRef.value.addEventListener('scroll', handleMobileScroll, scrollOptions);
      lastScrollY = mainRef.value.scrollTop;
      
      mainRef.value.addEventListener('touchstart', () => {
        scrollStartTime = Date.now();
      }, { passive: true });
      
      mainRef.value.addEventListener('touchend', () => {
        // Don't immediately reset scrolling state
        // Let the timeout handle it
      }, { passive: true });
    }
  }

  // Remove scroll listeners
  function removeScrollListeners() {
    if (mainRef.value) {
      mainRef.value.removeEventListener('scroll', handleMobileScroll);
      mainRef.value.removeEventListener('touchstart', () => {});
      mainRef.value.removeEventListener('touchend', () => {});
    }
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
  }

  // Watch for mobile state changes
  watch(() => uiStore.isMobile, (newIsMobile) => {
    isMobile.value = newIsMobile;
    if (newIsMobile && mainRef.value) {
      nextTick(() => {
        setupScrollListeners();
      });
    } else {
      removeScrollListeners();
    }
  });

  // Function to reset collapsed state
  function resetCollapsedState() {
    if (isHeaderCollapsed.value) {
      isHeaderCollapsed.value = false;
    }
    if (mainRef.value) {
      mainRef.value.scrollTop = 0;
    }
    // Reset bottom tracking state
    isAtBottomSticky = false;
    bottomReachedTime = 0;
    lastScrollY = 0;
  }

  // Watch for modal state changes - reset immediately when modal opens
  watch(() => {
    return document.querySelector('.mobile-page-template') !== null;
  }, (isModalOpen) => {
    if (isModalOpen) {
      // Immediately reset collapsed state when any modal opens
      nextTick(() => {
        resetCollapsedState();
      });
    }
  }, { immediate: true });

  // Also watch for other modal indicators (popup-wrapper, modal-main, etc.)
  const modalSelectors = [
    '.mobile-page-template',
    '.popup-wrapper.open',
    '.modal-main.open',
    '.mob-modal.open'
  ];

  function checkForOpenModals(): boolean {
    return modalSelectors.some(selector => {
      const element = document.querySelector(selector);
      return element !== null;
    });
  }

  // Watch for any modal opening using MutationObserver for better detection
  let modalObserver: MutationObserver | null = null;

  // Handle clicks on mob-list-item to reset state immediately (before modal opens)
  function handleListItemClick(event: Event) {
    const target = event.target as HTMLElement;
    if (target.closest('.mob-list-item')) {
      // Reset immediately when clicking on a list item (before modal opens)
      // Use setTimeout with 0 delay to ensure it runs before the modal opens
      setTimeout(() => {
        resetCollapsedState();
      }, 0);
    }
  }

  function setupModalObserver() {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    modalObserver = new MutationObserver(() => {
      if (checkForOpenModals()) {
        nextTick(() => {
          resetCollapsedState();
        });
      }
    });

    modalObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-open']
    });

    // Also listen for clicks on mob-list-item elements
    document.addEventListener('click', handleListItemClick, true);
  }

  function removeModalObserver() {
    if (modalObserver) {
      modalObserver.disconnect();
      modalObserver = null;
    }
    document.removeEventListener('click', handleListItemClick, true);
  }

  // Lifecycle hooks
  onMounted(async () => {
    await nextTick();
    setupScrollListeners();
    setupModalObserver();
  });

  onUnmounted(() => {
    removeScrollListeners();
    removeModalObserver();
  });

  return {
    mainRef,
    isHeaderCollapsed,
    isMobile
  };
}
